import { useRef, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Combobox from '@givebutter/primitives/combobox';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { Separator } from '@givebutter/primitives/separator';
import { fetchCampaigns } from 'api/campaigns';
import { RoleSettings } from 'assets/js/pages/account-settings/users/types';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { useIsPending } from 'customHooks/useIsPending';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Campaign } from 'types/domain';
import { PaginatedData } from 'types/pagination';

const CampaignSelect = () => {
    const name = 'campaigns';
    const [search, setSearch] = useState('');
    const listRef = useRef<HTMLDivElement>(null);
    const debouncedSearch = useDebounce(search);
    const { data, ref, isFetching } = useInfiniteCampaigns({ query: debouncedSearch });
    const campaigns = data?.pages ?? [];

    const { control, path, watch } = Form.useGivebutterFormContext<RoleSettings>();

    const formCampaigns = watch('campaigns');

    const { isPending, data: dCampaigns } = useSavedCampaigns(formCampaigns.map(c => Number(c)));

    return (
        <LoadingOverlay size={'sm'} isLoading={isPending} delay={2000}>
            <Form.Container>
                <Form.Label name={path(name)}>Limit access to selected campaigns</Form.Label>
                <Form.Combobox resource="campaign" control={control} name={name}>
                    <Combobox.Trigger aria-label="Select a campaign">
                        <Combobox.Select placeholder="Select a campaign" />
                    </Combobox.Trigger>
                    <Combobox.Popover>
                        <Combobox.Search
                            value={search}
                            onChangeValue={setSearch}
                            placeholder="Search"
                            isLoading={isFetching}
                        />
                        <Combobox.Items className={'text-sm font-medium'} ref={listRef}>
                            {dCampaigns.data.map(item => (
                                <Combobox.Item value={item.id.toString()} key={item.id}>
                                    <Combobox.CheckboxIndicator />
                                    <Combobox.ItemText>{item.title}</Combobox.ItemText>
                                </Combobox.Item>
                            ))}
                            {dCampaigns.data.length > 0 && <Separator className="my-1" />}
                            {campaigns.map(item => {
                                if (dCampaigns.data.some(c => c.id === item.id)) {
                                    return null;
                                }
                                return (
                                    <Combobox.Item value={item.id.toString()} key={item.id}>
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>{item.title}</Combobox.ItemText>
                                    </Combobox.Item>
                                );
                            })}
                            {campaigns.length === 0 ? (
                                <Combobox.NoneFoundText />
                            ) : (
                                <div ref={ref} />
                            )}
                        </Combobox.Items>
                    </Combobox.Popover>
                </Form.Combobox>
                <Form.Error name={path('campaigns')} />
                <p className={'text-sm text-gray-600'}>
                    The user will have access to <span className={'font-bold'}>all campaigns</span>{' '}
                    unless you limit them.
                </p>
            </Form.Container>
        </LoadingOverlay>
    );
};

const useSavedCampaigns = (ids: number[]) => {
    const { accountId } = useCurrentAccount();
    const [isPending, params] = useIsPending({
        ids,
        page: 1,
        per_page: ids.length,
    });

    const query = useSuspenseQuery({
        queryKey: accountQueryKeys.campaignsWithFilters(accountId, params),
        queryFn: () => {
            if (ids.length === 0) {
                return Promise.resolve({
                    data: [],
                    links: {} as any,
                    meta: {} as any,
                } as PaginatedData<Campaign>);
            }

            return fetchCampaigns(accountId, params);
        },
    });

    return {
        ...query,
        isPending,
    };
};

export { CampaignSelect };
